import uploadToS3Bucket from '@dehaat/kisan-app-bl/api/uploadToS3Bucket';
import { BUCKET_TYPE } from '@dehaat/kisan-app-bl/models/api/FileUpload';
import Image, { StaticImageData } from 'next/legacy/image';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { ChangeEvent, RefObject, useContext, useMemo, useRef, useState } from 'react';
import CenterModal from '@/components/CenterModal';
import ErrorModal from '@/components/ErrorModal';
import FarmTag from '@/components/farm/FarmTag';
import FourCornersFarmTag from '@/components/farm/FourCornersFarmTag';
import FeedBackScreen from '@/components/geoTag/FeedBackScreen';
import TakeCurrentLocation from '@/components/geoTag/TakeCurrentLocation';
import HeaderWithBackButton from '@/components/HeaderWithBackButton';
import CaretDownIcon from '@/components/icons/CaretDownIcon';
import Okay from '@/components/modalActions/Okay';
import Spinner from '@/components/Spinner';
import { COOKIE_MAX_AGE_1YEAR, LAT_LNG_COOKIE_NAME, NAVIGATION_FROM_APP, NAVIGATION_FROM_APP_CODE } from '@/constants/common';
import { UserContext } from '@/context/UserProvider';
import { useAppSelector } from '@/hooks/reduxHooks';
import useErrorModalState from '@/hooks/useErrorModalState';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import TagWithCentre from '@/images/prakshep/tag-with-centre.webp';
import TagWithFourCorner from '@/images/prakshep/tag-with-four-corner.webp';
import { selectLatLng } from '@/store/features/locationSlice';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
import { LocationType } from '@/utils/google';
import { getCurrentLocationValues } from '@/utils/google';
import { getCookieValue } from '@/utils/helper';
interface ActionCardProps {
  imageSrc: StaticImageData | string;
  heading: string;
  subheading: string;
  onClick?: () => void;
  farmInputRef?: RefObject<HTMLInputElement>;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
}
const ActionCard = ({
  imageSrc,
  heading,
  subheading,
  onClick,
  farmInputRef,
  onChange
}: ActionCardProps) => {
  return <div onClick={onClick} className="p-4 bg-white rounded-2xl shadow-md my-4 cursor-pointer hover:shadow-lg transition-shadow" data-sentry-component="ActionCard" data-sentry-source-file="prakshep.tsx">
      <Image src={imageSrc} alt="Dehaat Satellite Tagging" layout="responsive" width={2} height={1} priority data-sentry-element="Image" data-sentry-source-file="prakshep.tsx" />
      <input accept=".jpg, .jpeg, .png" className="hidden" data-testid="ask-question-disease-upload-input" onChange={onChange} ref={farmInputRef} type="file" capture="environment" />

      <div className="mt-3 justify-between flex flex-row items-center">
        <div>
          <h3 className="font-semibold text-base text-[#171B1F]">{heading}</h3>
          <p className="text-xs font-normal text-neutral-text-light-70">
            {subheading}
          </p>
        </div>

        <div className="p-[10px] bg-primary-100 rounded-xl justify-items-center items-center">
          <CaretDownIcon className="h-6 w-6 fill-transparent stroke-white stroke-2 -rotate-90" data-sentry-element="CaretDownIcon" data-sentry-source-file="prakshep.tsx" />
        </div>
      </div>
    </div>;
};
const OrDivider = () => {
  return <div className="flex items-center my-6" data-sentry-component="OrDivider" data-sentry-source-file="prakshep.tsx">
      <div className="flex-1 h-[1px] bg-neutral-200" />
      <span className="px-4 text-sm text-neutral-500 font-medium">OR</span>
      <div className="flex-1 h-[1px] bg-neutral-200" />
    </div>;
};
const Prakshep = () => {
  const {
    t
  } = useTranslation('prakshep');
  const {
    t: st
  } = useTranslation('satellite');
  const [showFarmTag, setShowFarmTag] = useState(false);
  const [showFourCornersFarmTag, setShowFourCornersFarmTag] = useState(false);
  const [showFeedBack, setShowFeedBack] = useState(false);
  const [farmId, setFarmId] = useState<number>();
  const {
    push
  } = useRouter();
  const {
    user
  } = useContext(UserContext);
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const farmInputRef = useRef<HTMLInputElement>(null);
  const [farmImage, setFarmImage] = useState<{
    file: Blob;
    filepath: string;
  }>();
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [isfileUploading, setIsFileUploading] = useState(false);
  const [location, setLocation] = useState<LocationType>();
  const {
    errorModalState,
    hideErrorModal,
    setErrorModalState
  } = useErrorModalState();
  const latLng = useAppSelector(selectLatLng);
  const onChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length === 1) {
      const file = e.target.files[0];
      const fileUploadRequestObj = {
        file,
        filePath: `PRAKSHEP/${user?.id}/`,
        bucketType: 'private' as BUCKET_TYPE
      };
      try {
        setIsFileUploading(true);
        const filepath = await uploadToS3Bucket(fileUploadRequestObj);
        if (filepath) {
          setFarmImage({
            file,
            filepath
          });
          const existingLocation = await getCurrentLocationValues();
          if (existingLocation) {
            setLocation(existingLocation);
          } else {
            setShowLocationModal(true);
          }
          trackCustomEvent(CustomEventTypes.SATELLITE_SELFIE_CLICKED, JSON.stringify({
            ...eventParams,
            filepath
          }));
        }
      } catch {
        setErrorModalState({
          ...errorModalState,
          message: t('image_upload_failed'),
          show: true
        });
      } finally {
        setIsFileUploading(false);
        e.target.value = '';
      }
      setShowFarmTag(true);
    }
  };
  const fromApp = useMemo(() => Boolean(getCookieValue(NAVIGATION_FROM_APP)) || Boolean(getCookieValue(NAVIGATION_FROM_APP_CODE)), []);
  const eventParams = user ? {
    FarmerName: user.full_name,
    FarmerNumber: user.phone_number
  } : {};
  return errorModalState?.show ? <ErrorModal {...errorModalState} onClose={hideErrorModal} data-sentry-element="ErrorModal" data-sentry-component="Prakshep" data-sentry-source-file="prakshep.tsx">
      <Okay handleClick={hideErrorModal} data-sentry-element="Okay" data-sentry-source-file="prakshep.tsx" />
    </ErrorModal> : <main className={`max-w-7xl m-auto relative bg-neutral-10 px-4  ${fromApp ? 'pb-12' : 'pb-24 pt-[60px]'} `} data-sentry-component="Prakshep" data-sentry-source-file="prakshep.tsx">
      {!fromApp && <HeaderWithBackButton heading={st('satellite_monitoring')} showHomeIcon={false} showProfileIcon={false} />}

      <>
        {isfileUploading && <Spinner />}
        <ActionCard imageSrc={TagWithCentre} heading="Tag with Centre" subheading="Go to centre of the farm and tag it." farmInputRef={farmInputRef} onChange={onChange} onClick={() => {
        trackCustomEvent(CustomEventTypes.SATELLITE_ADD_FARM_INTEREST, JSON.stringify({
          ...eventParams,
          source: 'prakshep'
        }));
        farmInputRef.current?.click();
        trackCustomEvent(CustomEventTypes.SATELLITE_ADD_FARM_REACHED, JSON.stringify(eventParams));
      }} data-sentry-element="ActionCard" data-sentry-source-file="prakshep.tsx" />
        <OrDivider data-sentry-element="OrDivider" data-sentry-source-file="prakshep.tsx" />
        <ActionCard imageSrc={TagWithFourCorner} heading="Tag with 4 corners" subheading="Go to 4 corners of the farm and tag it." onClick={() => setShowFourCornersFarmTag(true)} data-sentry-element="ActionCard" data-sentry-source-file="prakshep.tsx" />
      </>

      {showLocationModal && <CenterModal show={showLocationModal}>
          <TakeCurrentLocation getCurrentLatLong={location => {
        setLocation(location);
        if (!latLng) {
          document.cookie = `${LAT_LNG_COOKIE_NAME}=${`${location.latitude},${location.longitude}`}; max-age=${COOKIE_MAX_AGE_1YEAR}; path=/`;
        }
        setShowLocationModal(false);
      }} />
        </CenterModal>}

      {showFarmTag && <FarmTag onContinue={id => {
      setFarmId(id);
      setShowFeedBack(true);
      setShowFarmTag(false);
    }} onBackClick={() => setShowFarmTag(false)} farmImage={farmImage!} location={location!} />}

      {showFourCornersFarmTag && <FourCornersFarmTag onBackClick={() => setShowFourCornersFarmTag(false)} onContinue={id => {
      setFarmId(id);
      setShowFeedBack(true);
      setShowFourCornersFarmTag(false);
    }} />}
      {showFeedBack && farmId && <FeedBackScreen onContinue={() => {
      push('/farms');
    }} />}
    </main>;
};
export async function getStaticProps() {
  return {
    props: {
      title: 'Prakshep'
    }
  };
}
export default Prakshep;